/**
 * SegmentedBar — single horizontal bar split into 2–8 coloured segments.
 *
 * Extracted from `IhsVictoryProbability` (Phase 0b). Each segment carries
 * a width (`%`) and a colour. The whole bar exposes `role="img"` and an
 * aria-label that consumers supply (e.g. "Win probability shares").
 *
 * Cap raised from 4 → 8 in Phase 3b to support goal-timing distributions
 * across 6 × 15-minute windows and other higher-arity splits. With
 * narrow viewports (~340px) and many segments, the per-segment minimum
 * width gets very small — consumers should ensure visually distinct
 * colours and provide segment-level `label` for screen readers.
 *
 * Shape:
 *   { segments: Array<{ width: number; color: string; label?: string }>;
 *     ariaLabel: string }
 */

import type { ReactElement } from 'react';
import { z } from 'zod';
import type { PrimitiveMeta } from './_meta.js';
import styles from './SegmentedBar.module.css';

const dataShape = z.object({
  segments: z
    .array(
      z.object({
        /** Width in percent (0..100). Consumers normalise — primitive trusts. */
        width: z.number().min(0).max(100),
        color: z.string(),
        label: z.string().optional(),
      }),
    )
    .min(2)
    .max(8),
  ariaLabel: z.string(),
});

export const META: PrimitiveMeta = {
  name: 'segmented-bar',
  description: 'Single horizontal bar split into 2-8 coloured segments.',
  composable: true,
  dataShape,
  densities: ['standard'],
  breakpoints: { mobile: true, wide: true },
  animation: 'none',
};

export interface SegmentedBarSegment {
  /** Segment width in percent (0..100). Consumers normalise — primitive trusts. */
  width: number;
  color: string;
  /** Optional aria-label per segment for richer SR output. */
  label?: string;
}

interface SegmentedBarProps {
  segments: SegmentedBarSegment[];
  ariaLabel: string;
}

export function SegmentedBar({
  segments,
  ariaLabel,
}: SegmentedBarProps): ReactElement {
  return (
    <div className={styles.bar} role="img" aria-label={ariaLabel}>
      {segments.map((segment, i) => (
        <span
          key={i}
          className={styles.segment}
          style={{
            width: `${segment.width}%`,
            background: segment.color,
          }}
          {...(segment.label !== undefined ? { 'aria-label': segment.label } : {})}
        />
      ))}
    </div>
  );
}

export default SegmentedBar;
